import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useStableValue } from '@ipfabric/ui-components/hooks/useStableValue';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import {
  type EdgeId,
  type NodeSn,
  type PacketHeader,
  type PathLookupDeviceDecisionsMap,
  type PathLookupGraphPreset,
  isNonNullable,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import type {
  DeviceViewNodeEntity,
  PacketDetail as IPacketDetail,
  PathInspectorPackets,
  PathLookupPacketDiff,
  PathLookupPathInspectorSelectedPacketSource,
  PathLookupPathInspectorSelectedPackets,
} from 'components/DeviceView/deviceViewTypes';
import { useLocale } from 'containers/App/LocaleContext';
import { detailedDiff } from 'deep-object-diff';
import { type FC, type RefObject, memo, useRef, useState } from 'react';
import type * as React from 'react';
import type { Scrollbars } from 'react-custom-scrollbars-2';
import type { AppTranslations } from 'translations/translations';
import { PacketDetail } from '../PacketDetail/PacketDetail';
import type { PathInspectorPacketSelectEvent } from './PathInspectorPacketDetail';
import { PathInspectorSelectPacketFromList } from './PathInspectorSelectPacketFromList';
import { PathInspectorTable } from './PathInspectorTable';
import { PathInspectorTablePlaceholder } from './PathInspectorTablePlaceholder';
import { getPacketSeverityInfo } from './helpers/getPacketSeverityInfo';

import { useDeviceViewContextSelectors } from 'components/DeviceView/model/DeviceViewModel';

const getPacketTitle = (
  msg: AppTranslations,
  packetSource: PathLookupPathInspectorSelectedPacketSource | null,
): string | undefined => {
  switch (packetSource) {
    case 'acceptedPackets':
      return msg.deviceView.devicePathLookup.acceptedPacket;
    case 'droppedPackets':
      return msg.deviceView.devicePathLookup.droppedPacket;
    case 'generatedPackets':
      return msg.deviceView.devicePathLookup.generatedPacket;
    default:
      return undefined;
  }
};

const hasPacketDifferentHeaders = (
  otherPacketIndex: number,
  currentPacketHeader: PacketHeader,
  otherPacketHeader: PacketHeader,
) =>
  otherPacketIndex >= 0 && currentPacketHeader.type !== otherPacketHeader.type;

// If first or second packet has more headers,
// add missing header to get valid indexes in compare.
const extendPacketForCompare = (
  currentPacket: PacketHeader[],
  otherPacket: PacketHeader[] | undefined,
  type: 'first' | 'second',
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
): (PacketHeader | null)[] => {
  if (currentPacket === undefined || otherPacket === undefined) {
    return currentPacket;
  }
  const extendedCurrentPacket: (PacketHeader | null)[] = [];
  let currentPacketIndex = currentPacket.length - 1;
  let otherPacketIndex = otherPacket.length - 1;
  // Compare headers for packets and add placeholder for missing header.
  // Search from end. API adds added/removed headers to the start of packet.
  while (currentPacketIndex >= 0) {
    // Fixed noUncheckedIndexedAccess NIM-19408
    const currentPacketItem = currentPacket[currentPacketIndex]!;
    // Fixed noUncheckedIndexedAccess NIM-19408
    const otherPacketItem = otherPacket[otherPacketIndex]!;

    // Comparing on different packet headers positions
    if (
      hasPacketDifferentHeaders(
        otherPacketIndex,
        currentPacketItem,
        otherPacketItem,
      )
    ) {
      // More headers on other side
      if (currentPacketIndex < otherPacketIndex) {
        extendedCurrentPacket.unshift(null);
        otherPacketIndex -= 1;
        continue;
      }
      // More headers on current side
      if (currentPacketIndex > otherPacketIndex) {
        currentPacketIndex -= 1;
        extendedCurrentPacket.unshift(currentPacketItem);
        continue;
      }
    }

    // Comparing on the same packet headers positions
    if (
      hasPacketDifferentHeaders(
        otherPacketIndex,
        currentPacketItem,
        otherPacketItem,
      ) &&
      type === 'first'
    ) {
      extendedCurrentPacket.unshift(null);
    }
    extendedCurrentPacket.unshift(currentPacketItem);
    if (
      hasPacketDifferentHeaders(
        otherPacketIndex,
        currentPacketItem,
        otherPacketItem,
      ) &&
      type === 'second'
    ) {
      extendedCurrentPacket.unshift(null);
    }
    currentPacketIndex -= 1;
    otherPacketIndex -= 1;
  }
  // If other packet has more headers, add placeholder for missing headers
  while (otherPacketIndex >= 0) {
    extendedCurrentPacket.unshift(null);
    otherPacketIndex -= 1;
  }
  return extendedCurrentPacket;
};

interface Props {
  decisions: PathLookupDeviceDecisionsMap;
  deviceViewEntity: DeviceViewNodeEntity;
  deviceViewEntityNodeSn: NodeSn;
  hoveredPathInspectorEdge?: EdgeId | null;
  nodeTitle: string;
  pathInspectorPackets: PathInspectorPackets;
  pathLookupPathInspectorSelectedPackets: PathLookupPathInspectorSelectedPackets;
  pathLookupPreset: PathLookupGraphPreset;
  scrollbar: RefObject<Scrollbars>;
}

/**
 * Find missing protocol in packet.
 * If protocol is missing, extend diff with other packet protocol attributes
 * to show missing protocol as removed or added.
 */
const extendDiffWithAddedRemovedProtocols = (
  diff: PathLookupPacketDiff,
  currentPacketHeaders: (PacketHeader | null)[],
  otherPacketHeaders: (PacketHeader | null)[],
  type: 'added' | 'deleted',
): void => {
  currentPacketHeaders.forEach((currentPacketHeader, index) => {
    if (currentPacketHeader === null) {
      const otherPacketHeader = otherPacketHeaders[index];
      if (otherPacketHeader !== undefined && otherPacketHeader !== null) {
        if (diff[type] === undefined) {
          diff[type] = {};
        }
        diff[type][index] = {};
        for (const key of Object.keys(otherPacketHeader)) {
          // Fixed noUncheckedIndexedAccess NIM-19408
          diff[type][index]![key] = undefined;
        }
      }
    }
  });
};

/**
 * Get diff for both incoming and outgoing packet.
 * deep-object-diff has bug that it returns invalid indexes when both sides have different number of keys.
 * We must add info about added/deleted protocols manually.
 */
const getEdgesDiff = (
  firstEdge: IPacketDetail | null,
  secondEdge: IPacketDetail | null,
): PathLookupPacketDiff | undefined => {
  if (!firstEdge || !firstEdge.packet || !secondEdge || !secondEdge.packet) {
    return undefined;
  }
  const extendedFirstPacket = extendPacketForCompare(
    firstEdge.packet,
    secondEdge.packet,
    'first',
  );
  const extendedSecondPacket = extendPacketForCompare(
    secondEdge.packet,
    firstEdge.packet,
    'second',
  );
  const diff = detailedDiff(
    extendedFirstPacket,
    extendedSecondPacket,
  ) as PathLookupPacketDiff;

  extendDiffWithAddedRemovedProtocols(
    diff,
    extendedFirstPacket,
    extendedSecondPacket,
    'added',
  );

  extendDiffWithAddedRemovedProtocols(
    diff,
    extendedSecondPacket,
    extendedFirstPacket,
    'deleted',
  );

  return diff;
};

const BarePathInspector: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    dispatch: prop('dispatch'),
    handlersContainer: prop('handlersContainer'),
  });
  const msg = useLocale();
  const dragListeners = useStableValue(() => new ManagedDomEventListeners());
  const [pathInspectorContentDragging, setPathInspectorContentDragging] =
    useState<boolean>(false);
  const mousePosition = useRef<number>(0);

  // Fixed noUncheckedIndexedAccess NIM-19408
  const selectedIncomingPacket =
    props.pathLookupPathInspectorSelectedPackets[props.deviceViewEntityNodeSn]
      ?.selectedIncomingPacket!;
  // Fixed noUncheckedIndexedAccess NIM-19408
  const selectedFirstEdgeSource =
    props.pathLookupPathInspectorSelectedPackets[props.deviceViewEntityNodeSn]
      ?.selectedIncomingPacketSource!;
  // Fixed noUncheckedIndexedAccess NIM-19408
  const selectedOutgoingPacket =
    props.pathLookupPathInspectorSelectedPackets[props.deviceViewEntityNodeSn]
      ?.selectedOutgoingPacket!;
  // Fixed noUncheckedIndexedAccess NIM-19408
  const selectedOutgoingPacketSource =
    props.pathLookupPathInspectorSelectedPackets[props.deviceViewEntityNodeSn]
      ?.selectedOutgoingPacketSource!;

  const diff = getEdgesDiff(selectedIncomingPacket, selectedOutgoingPacket);

  const pathInspectorNodePackets =
    props.pathInspectorPackets[props.deviceViewEntityNodeSn];

  const handleContentMouseUp = useStableCallback(() => {
    dragListeners.remove();
    setPathInspectorContentDragging(false);
  });

  const handlePathInspectorMouseDown = useStableCallback(
    (e: React.MouseEvent) => {
      const scrollValues = props.scrollbar.current?.getValues();
      if (scrollValues !== undefined) {
        const { clientWidth, scrollWidth } = scrollValues;
        // Enable content drag only if scroll is available
        if (scrollWidth > clientWidth) {
          dragListeners.add(
            [window, 'mousemove', handleContentMouseMove],
            [window, 'mouseup', handleContentMouseUp],
          );
          mousePosition.current = e.clientX;
        }
      }
    },
  );

  const handleContentMouseMove = useStableCallback((e: MouseEvent) => {
    setPathInspectorContentDragging(true);
    if (props.scrollbar.current !== null) {
      const scrollDiff = mousePosition.current - e.clientX;
      props.scrollbar.current.scrollLeft(
        props.scrollbar.current.getScrollLeft() + scrollDiff,
      );
      mousePosition.current = e.clientX;
    }
  });

  const handlePathInspectorPacketSelect = useStableCallback(
    (event: PathInspectorPacketSelectEvent) => {
      // If user selected text in packet, don't select packet to allow copy-paste.
      if (
        !pathInspectorContentDragging &&
        !window.getSelection()?.toString().length
      ) {
        ctxProps.handlersContainer.handlePathInspectorPacketSelect?.(event);
      }
      setPathInspectorContentDragging(false);
    },
  );

  return (
    <div
      className={classNames(
        'flex flex-1',
        pathInspectorContentDragging && 'pointer-events-none select-none',
      )}
      onMouseDown={handlePathInspectorMouseDown}
    >
      {selectedIncomingPacket ? (
        <PacketDetail
          detailId={selectedIncomingPacket.id}
          deviceViewElement='node'
          deviceViewEntityNodeSn={props.deviceViewEntityNodeSn}
          diff={diff}
          disableHover={false}
          edgeDiffType='removed'
          edgeDirection={selectedIncomingPacket.edgeDirection}
          hasPreviousPathLookupNode={
            selectedIncomingPacket.hasPreviousPathLookupNode
          }
          labels={selectedIncomingPacket.labels}
          onPacketDetailClose={
            ctxProps.handlersContainer.handleRemovePacketDetail
          }
          onPacketDetailHover={
            ctxProps.handlersContainer.handlePacketDetailHover
          }
          onPacketDetailLeave={
            ctxProps.handlersContainer.handlePacketDetailLeave
          }
          onPreviousButtonClick={
            ctxProps.handlersContainer.handlePacketDetailPreviousButtonClick
          }
          onPreviousButtonMouseEnter={
            ctxProps.handlersContainer
              .handlePacketDetailPreviousButtonMouseEnter
          }
          onPreviousButtonMouseLeave={
            ctxProps.handlersContainer
              .handlePacketDetailPreviousButtonMouseLeave
          }
          packet={extendPacketForCompare(
            selectedIncomingPacket.packet,
            selectedOutgoingPacket?.packet,
            'first',
          )}
          pathLookupPreset={props.pathLookupPreset}
          showAnimation={false}
          showEmptyPlaceholder={false}
          sourceLabel={selectedIncomingPacket.sourceLabel}
          targetLabel={selectedIncomingPacket.targetLabel}
          title={getPacketTitle(msg, selectedFirstEdgeSource)}
          trafficScore={selectedIncomingPacket.trafficScore}
        />
      ) : (
        <PathInspectorSelectPacketFromList
          deviceViewEntity={props.deviceViewEntity}
          hoveredPathInspectorEdge={props.hoveredPathInspectorEdge}
          onSelectPacketHover={
            ctxProps.handlersContainer.handleSelectPacketHover
          }
          onSelectPacketLeave={
            ctxProps.handlersContainer.handleSelectPacketLeave
          }
          onPathInspectorPacketSelect={handlePathInspectorPacketSelect}
          pathInspectorNodePackets={pathInspectorNodePackets}
          pathLookupPreset={props.pathLookupPreset}
          selectType='first'
        />
      )}
      {selectedIncomingPacket &&
        selectedOutgoingPacket &&
        isNonNullable(props.deviceViewEntity.deviceId) && (
          <PathInspectorTable
            decision={props.decisions[props.deviceViewEntity.deviceId]}
            deviceViewEntity={props.deviceViewEntity}
            nodeTitle={props.nodeTitle}
            severityInfo={
              isNonNullable(selectedOutgoingPacket?.id)
                ? getPacketSeverityInfo(
                    selectedIncomingPacket.id,
                    selectedOutgoingPacket.id,
                    props.decisions[props.deviceViewEntity.deviceId],
                  )
                : undefined
            }
            sourcePacketId={selectedIncomingPacket.id}
            targetPacketId={selectedOutgoingPacket.id}
          />
        )}
      {selectedIncomingPacket && !selectedOutgoingPacket && (
        <PathInspectorTablePlaceholder nodeTitle={props.nodeTitle} />
      )}
      {selectedOutgoingPacket && (
        <PacketDetail
          detailId={selectedOutgoingPacket.id}
          deviceViewElement='node'
          deviceViewEntityNodeSn={props.deviceViewEntity.sn}
          diff={diff}
          disableHover={false}
          edgeDiffType='added'
          edgeDirection={selectedOutgoingPacket.edgeDirection}
          hasNextPathLookupNode={selectedOutgoingPacket.hasNextPathLookupNode}
          labels={selectedOutgoingPacket.labels}
          onNextButtonClick={
            ctxProps.handlersContainer.handlePacketDetailNextButtonClick
          }
          onNextButtonMouseEnter={
            ctxProps.handlersContainer.handlePacketDetailNextButtonMouseEnter
          }
          onNextButtonMouseLeave={
            ctxProps.handlersContainer.handlePacketDetailNextButtonMouseLeave
          }
          onPacketDetailClose={
            ctxProps.handlersContainer.handleRemovePacketDetail
          }
          onPacketDetailHover={
            ctxProps.handlersContainer.handlePacketDetailHover
          }
          onPacketDetailLeave={
            ctxProps.handlersContainer.handlePacketDetailLeave
          }
          packet={extendPacketForCompare(
            selectedOutgoingPacket.packet,
            selectedIncomingPacket?.packet,
            'second',
          )}
          pathLookupPreset={props.pathLookupPreset}
          showAnimation={false}
          showEmptyPlaceholder
          sourceLabel={selectedOutgoingPacket.sourceLabel}
          targetLabel={selectedOutgoingPacket.targetLabel}
          title={getPacketTitle(msg, selectedOutgoingPacketSource)}
        />
      )}
      {selectedIncomingPacket &&
        !selectedOutgoingPacket &&
        isNonNullable(props.deviceViewEntity.deviceId) && (
          <PathInspectorSelectPacketFromList
            decision={props.decisions[props.deviceViewEntity.deviceId]}
            deviceViewEntity={props.deviceViewEntity}
            hoveredPathInspectorEdge={props.hoveredPathInspectorEdge}
            onSelectPacketHover={
              ctxProps.handlersContainer.handleSelectPacketHover
            }
            onSelectPacketLeave={
              ctxProps.handlersContainer.handleSelectPacketLeave
            }
            onPathInspectorPacketSelect={handlePathInspectorPacketSelect}
            pathInspectorNodePackets={pathInspectorNodePackets}
            pathLookupPreset={props.pathLookupPreset}
            selectType='second'
            sourcePacketId={selectedIncomingPacket?.id}
          />
        )}
    </div>
  );
};

export const PathInspector = memo(BarePathInspector);
